import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  ClipboardCheck, Plus, Loader2, Trash2, Pencil, FileUp, FileText,
  ChevronRight, Link2, Unlink, Star, Save, BookOpen, Download,
  Trash, RotateCcw, History, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { ListSkeleton } from "../components/Skeletons";

const inputCls = "bg-[#0b0811] border border-white/10 text-white placeholder:text-slate-500 focus:border-[#f7c935]";

export default function AdminAuditsPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [tab, setTab] = useState("audits");
  const [audits, setAudits] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [accompagnements, setAccompagnements] = useState([]);
  const [trashAudits, setTrashAudits] = useState([]);
  const [trashTemplates, setTrashTemplates] = useState([]);
  const [trail, setTrail] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editAudit, setEditAudit] = useState(null);
  const [showFromTemplate, setShowFromTemplate] = useState(false);
  const [deleteCtx, setDeleteCtx] = useState(null); // {type, id, title}

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [a, t, c, ac, ta, tt, tr] = await Promise.all([
        api.get("/audits"),
        api.get("/audit-templates"),
        api.get("/admin/clients"),
        api.get("/accompaniments"),
        api.get("/audits/trash"),
        api.get("/audit-templates/trash"),
        api.get("/audits/trail"),
      ]);
      setAudits(a.data || []);
      setTemplates(t.data || []);
      setClients(c.data || []);
      setAccompagnements(ac.data || []);
      setTrashAudits(ta.data || []);
      setTrashTemplates(tt.data || []);
      setTrail(tr.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  // eslint-disable-next-line
  }, []);

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const clientName = (uid) => clients.find(c => c.user_id === uid)?.name || clients.find(c => c.user_id === uid)?.email || uid;

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="admin-audits-page">
      <PageHeader
        icon={<ClipboardCheck />}
        title="Audits & Modèles"
        description="Importez des modèles DOCX, créez des audits structurés, rattachez-les aux accompagnements"
        testId="admin-audits-header"
      />

      <div className="flex gap-2 mb-6 border-b border-white/10">
        {[
          { k: "audits", l: "Audits clients", icon: ClipboardCheck },
          { k: "templates", l: "Modeles (DOCX)", icon: BookOpen },
          { k: "trash", l: `Corbeille (${trashAudits.length + trashTemplates.length})`, icon: Trash },
          { k: "trail", l: "Journal RGPD", icon: History },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            data-testid={`audits-tab-${t.k}`}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold transition-colors ${tab === t.k ? "text-[#f7c935] border-b-2 border-[#f7c935]" : "text-slate-400 hover:text-white"}`}>
            <t.icon className="w-4 h-4" /> {t.l}
          </button>
        ))}
      </div>

      {loading && <ListSkeleton items={5} testId="audits-loading-skeleton" />}

      {!loading && tab === "audits" && (
        <AuditsList
          audits={audits}
          templates={templates}
          clientName={clientName}
          onOpen={setEditAudit}
          onFromTemplate={() => setShowFromTemplate(true)}
          onDelete={(id, title) => setDeleteCtx({ type: "audit", id, title })}
        />
      )}

      {!loading && tab === "templates" && (
        <TemplatesList
          templates={templates}
          api={api}
          reload={loadAll}
          onDelete={(id, name) => setDeleteCtx({ type: "template", id, title: name })}
        />
      )}

      {!loading && tab === "trash" && (
        <TrashTab
          trashAudits={trashAudits}
          trashTemplates={trashTemplates}
          clientName={clientName}
          api={api}
          reload={loadAll}
        />
      )}

      {!loading && tab === "trail" && <TrailTab trail={trail} clientName={clientName} />}

      {editAudit && (
        <AuditEditor
          audit={editAudit}
          clients={clients}
          accompagnements={accompagnements.filter(a => a.client_id === editAudit.client_id)}
          api={api}
          onClose={() => { setEditAudit(null); loadAll(); }}
        />
      )}

      {showFromTemplate && (
        <FromTemplateDialog
          templates={templates}
          clients={clients}
          accompagnements={accompagnements}
          api={api}
          onClose={(created) => { setShowFromTemplate(false); if (created) loadAll(); }}
        />
      )}

      {deleteCtx && (
        <DeleteMotifDialog
          ctx={deleteCtx}
          api={api}
          onClose={(deleted) => { setDeleteCtx(null); if (deleted) loadAll(); }}
        />
      )}
    </div>
  );
}

// ─── Audits list ───────────────────────────────────────────
function AuditsList({ audits, templates, clientName, onOpen, onDelete, onFromTemplate }) {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();

  const downloadAuditPdf = async (auditId, title) => {
    try {
      const r = await api.get(`/audits/${auditId}/export-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `audit-${(title || "export").replace(/\s/g, "-")}.pdf`;
      document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={onFromTemplate} disabled={templates.length === 0} className="bg-[#f7c935] text-[#14101c] font-bold hover:bg-[#ffd948]" data-testid="create-audit-from-template-btn">
          <Plus className="w-4 h-4 mr-2" /> Creer un audit depuis un modele
        </Button>
      </div>
      {templates.length === 0 && audits.length === 0 && (
        <div className="text-center py-10 text-slate-500 border border-dashed border-white/10 rounded-xl">
          Importez d'abord un modele DOCX depuis l'onglet "Modeles" pour commencer.
        </div>
      )}
      {audits.length === 0 ? null : (
        <div className="grid gap-3">
          {audits.map(a => (
            <div key={a.audit_id} className="bg-[#14101c] border border-white/10 rounded-xl p-5 flex items-center justify-between" data-testid={`audit-${a.audit_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <ClipboardCheck className="w-4 h-4 text-[#f7c935]" />
                  <span className="font-bold text-white">{a.title}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-slate-400">{a.status}</span>
                  {a.accompaniment_id && <span className="text-[10px] px-2 py-0.5 rounded bg-[#f7c935]/10 text-[#f7c935] flex items-center gap-1"><Link2 className="w-3 h-3" />Lie</span>}
                  {a.mode && <span className="text-[10px] px-2 py-0.5 rounded bg-white/5">{a.mode}</span>}
                </div>
                <div className="text-xs text-slate-400">Client: {clientName(a.client_id)} · {a.sections?.length || 0} sections</div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => onOpen(a)} className="text-[#f7c935]" data-testid={`open-audit-${a.audit_id}`}><ChevronRight className="w-4 h-4" /> Ouvrir</Button>
                <Button size="sm" variant="ghost" onClick={() => downloadAuditPdf(a.audit_id, a.title)} className="text-slate-300 hover:text-[#f7c935]" data-testid={`export-pdf-${a.audit_id}`}><Download className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => onDelete(a.audit_id, a.title)} className="text-red-400" data-testid={`delete-audit-${a.audit_id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Templates list + DOCX import ──────────────────────────
function TemplatesList({ templates, api, reload, onDelete }) {
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/audit-templates/import?name=${encodeURIComponent(file.name)}&mode=mixed`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Importe: ${r.data.sections?.length || 0} sections, ${r.data.detected_variables?.length || 0} balises`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setUploading(false); }
  };

  const del = async (id, name) => {
    onDelete(id, name);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <label className="inline-flex items-center gap-2 cursor-pointer bg-[#f7c935] text-[#14101c] font-bold px-4 py-2 rounded hover:bg-[#ffd948]" data-testid="import-template-btn">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
          {uploading ? "Import..." : "Importer DOCX / PDF"}
          <input type="file" accept=".docx,.pdf" className="hidden" onChange={e => e.target.files?.[0] && upload(e.target.files[0])} />
        </label>
      </div>
      {templates.length === 0 ? (
        <div className="text-center py-16 text-slate-500 border border-dashed border-white/10 rounded-xl">
          <FileText className="w-12 h-12 mx-auto text-slate-700 mb-3" />
          Aucun modele. Importez un DOCX pour demarrer — le parser detecte automatiquement les titres (Heading 1/2) comme sections.
        </div>
      ) : (
        <div className="grid gap-3">
          {templates.map(t => (
            <div key={t.template_id} className="bg-[#14101c] border border-white/10 rounded-xl p-4" data-testid={`template-${t.template_id}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-[#f7c935]" />
                    <span className="font-bold">{t.name}</span>
                    {t.category && <span className="text-[10px] px-2 py-0.5 rounded bg-white/5">{t.category}</span>}
                    <span className="text-[10px] px-2 py-0.5 rounded bg-white/5">{t.mode}</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-1">{t.sections?.length || 0} sections · {t.detected_variables?.length || 0} balises detectees {t.source_filename && `· source: ${t.source_filename}`}</div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => del(t.template_id, t.name)} className="text-red-400" data-testid={`del-template-${t.template_id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
              {/* Preview sections */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                {(t.sections || []).slice(0, 6).map((s, i) => (
                  <div key={i} className="bg-[#0b0811] border border-white/5 rounded p-2">
                    <div className="text-xs font-bold text-[#f7c935]">{s.heading}</div>
                    <ul className="text-[11px] text-slate-500 mt-1 space-y-0.5">
                      {(s.items || []).slice(0, 3).map((it, j) => <li key={j} className="truncate">• {it.text}</li>)}
                      {(s.items?.length || 0) > 3 && <li className="text-slate-600">... +{s.items.length - 3}</li>}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Create audit from template dialog ─────────────────────
function FromTemplateDialog({ templates, clients, accompagnements, api, onClose }) {
  const [clientId, setClientId] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [accompanimentId, setAccompanimentId] = useState("");
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);

  const clientAccs = accompagnements.filter(a => a.client_id === clientId);

  const save = async () => {
    if (!clientId || !templateId) { toast.error("Client et modele requis"); return; }
    setSaving(true);
    try {
      await api.post("/audits/from-template", {
        client_id: clientId,
        template_id: templateId,
        accompaniment_id: accompanimentId || null,
        title: title || null,
      });
      toast.success("Audit cree");
      onClose(true);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(false); }}>
      <DialogContent className="max-w-lg bg-[#0b0811] border border-white/10 text-white">
        <DialogHeader><DialogTitle>Creer un audit depuis un modele</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Client</label>
            <select value={clientId} onChange={e => { setClientId(e.target.value); setAccompanimentId(""); }} className={`${inputCls} px-3 py-2 rounded w-full`} data-testid="ft-client">
              <option value="">— Selectionner —</option>
              {clients.map(c => <option key={c.user_id} value={c.user_id}>{c.name || c.email} {c.company_name ? `(${c.company_name})` : ""}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Modele</label>
            <select value={templateId} onChange={e => setTemplateId(e.target.value)} className={`${inputCls} px-3 py-2 rounded w-full`} data-testid="ft-template">
              <option value="">— Selectionner —</option>
              {templates.map(t => <option key={t.template_id} value={t.template_id}>{t.name} ({t.sections?.length || 0} sections)</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Accompagnement (optionnel)</label>
            <select value={accompanimentId} onChange={e => setAccompanimentId(e.target.value)} disabled={!clientId || clientAccs.length === 0} className={`${inputCls} px-3 py-2 rounded w-full disabled:opacity-50`} data-testid="ft-accompaniment">
              <option value="">— Aucun —</option>
              {clientAccs.map(a => <option key={a.accompaniment_id} value={a.accompaniment_id}>{a.title}</option>)}
            </select>
            {clientId && clientAccs.length === 0 && <div className="text-[10px] text-slate-500 mt-1">Ce client n'a aucun accompagnement actif.</div>}
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Titre (optionnel)</label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Ex: Audit Q2 2026" className={inputCls} data-testid="ft-title" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="ghost" onClick={() => onClose(false)}>Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="ft-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} Creer l'audit
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Audit Editor (fill sections / scored items) ───────────
function AuditEditor({ audit, clients, accompagnements, api, onClose }) {
  const [sections, setSections] = useState(audit.sections || []);
  const [linkedAccId, setLinkedAccId] = useState(audit.accompaniment_id || "");
  const [saving, setSaving] = useState(false);

  const updateItem = (si, ii, patch) => {
    const copy = [...sections];
    copy[si] = { ...copy[si], items: [...copy[si].items] };
    copy[si].items[ii] = { ...copy[si].items[ii], ...patch };
    setSections(copy);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/audits/${audit.audit_id}/sections`, { sections });
      toast.success("Audit enregistre");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  const toggleLink = async () => {
    try {
      if (linkedAccId) {
        await api.post(`/audits/${audit.audit_id}/attach`, { accompaniment_id: linkedAccId });
        toast.success("Audit rattache");
      } else {
        await api.post(`/audits/${audit.audit_id}/detach`);
        toast.success("Audit detache");
      }
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const exportPdf = async () => {
    try {
      const r = await api.get(`/audits/${audit.audit_id}/export-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `audit-${(audit.title || "export").replace(/\s/g, "-")}.pdf`;
      document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      toast.success("PDF telecharge");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl bg-[#0b0811] border border-white/10 text-white" data-testid="audit-editor">
        <DialogHeader><DialogTitle>{audit.title}</DialogTitle></DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto pr-2 space-y-4">
          {/* Link to accompaniment */}
          <div className="bg-[#14101c] border border-white/10 rounded-lg p-3 flex items-center gap-2">
            <Link2 className="w-4 h-4 text-[#f7c935]" />
            <span className="text-xs text-slate-400">Rattacher a l'accompagnement :</span>
            <select value={linkedAccId} onChange={e => setLinkedAccId(e.target.value)} className={`${inputCls} px-2 py-1 text-xs rounded flex-1`} data-testid="audit-attach-select">
              <option value="">— Aucun —</option>
              {accompagnements.map(a => <option key={a.accompaniment_id} value={a.accompaniment_id}>{a.title}</option>)}
            </select>
            <Button size="sm" onClick={toggleLink} className="bg-[#f7c935] text-[#14101c] font-bold text-xs" data-testid="audit-toggle-link">
              {linkedAccId ? <><Link2 className="w-3 h-3 mr-1" />Rattacher</> : <><Unlink className="w-3 h-3 mr-1" />Detacher</>}
            </Button>
          </div>
          {/* Sections */}
          {(sections || []).map((s, si) => (
            <div key={s.section_id || si} className="bg-[#14101c] border border-white/10 rounded-lg p-4">
              <h3 className="font-bold text-[#f7c935] mb-3">{s.heading}</h3>
              <div className="space-y-3">
                {(s.items || []).map((it, ii) => (
                  <div key={it.item_id || ii} className="bg-[#0b0811] border border-white/5 rounded p-3" data-testid={`audit-item-${si}-${ii}`}>
                    <div className="text-sm font-semibold mb-2">{it.label}</div>
                    {/* Mode switcher for mixed templates */}
                    {audit.mode === "mixed" && (
                      <div className="flex gap-2 mb-2 text-[10px]">
                        <button onClick={() => updateItem(si, ii, { mode: "freeform" })} className={`px-2 py-0.5 rounded ${it.mode === "freeform" ? "bg-[#f7c935] text-[#14101c]" : "bg-white/5 text-slate-400"}`}>Texte libre</button>
                        <button onClick={() => updateItem(si, ii, { mode: "scored" })} className={`px-2 py-0.5 rounded ${it.mode === "scored" ? "bg-[#f7c935] text-[#14101c]" : "bg-white/5 text-slate-400"}`}>Scoring 1-5</button>
                      </div>
                    )}
                    {/* Scored 1-5 */}
                    {(audit.mode === "scored" || (audit.mode === "mixed" && it.mode === "scored")) && (
                      <div className="flex gap-1 mb-2">
                        {[1, 2, 3, 4, 5].map(n => (
                          <button key={n} onClick={() => updateItem(si, ii, { score: n })} className={`w-8 h-8 rounded ${it.score === n ? "bg-[#f7c935] text-[#14101c]" : "bg-white/5 text-slate-400"}`} data-testid={`score-${si}-${ii}-${n}`}>
                            <Star className={`w-4 h-4 mx-auto ${it.score === n ? "fill-current" : ""}`} />
                          </button>
                        ))}
                      </div>
                    )}
                    <Textarea placeholder="Commentaire / note" value={it.note || ""} onChange={e => updateItem(si, ii, { note: e.target.value })} rows={2} className={inputCls} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="ghost" onClick={onClose}>Fermer</Button>
          <Button onClick={exportPdf} variant="outline" className="border-white/10 text-white hover:bg-white/5" data-testid="audit-export-pdf-btn">
            <Download className="w-4 h-4 mr-2" /> Export PDF
          </Button>
          <Button onClick={save} disabled={saving} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="audit-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Enregistrer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Trash Tab (RGPD) ──────────────────────────────────────
function TrashTab({ trashAudits, trashTemplates, clientName, api, reload }) {
  const restore = async (type, id) => {
    try {
      await api.post(`/${type === "audit" ? "audits" : "audit-templates"}/${id}/restore`);
      toast.success("Restaure");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const purge = async (type, id) => {
    if (!window.confirm("Purger definitivement ? Cette action est irreversible.")) return;
    try {
      await api.delete(`/${type === "audit" ? "audits" : "audit-templates"}/${id}/purge`);
      toast.success("Purge definitivement");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const isEmpty = trashAudits.length === 0 && trashTemplates.length === 0;

  return (
    <div className="space-y-6" data-testid="trash-tab">
      <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
        <div className="text-xs text-slate-300">
          <div className="font-bold text-yellow-400 mb-1">Tracabilite RGPD</div>
          Les elements supprimes sont conserves ici avec l'auteur, la date et le motif. Restaurez pour recuperer, ou purgez pour supprimer definitivement. Consultez le <b>Journal RGPD</b> pour l'historique complet.
        </div>
      </div>
      {isEmpty && (
        <div className="text-center py-16 text-slate-500 border border-dashed border-white/10 rounded-xl">
          <Trash className="w-12 h-12 mx-auto text-slate-700 mb-3" />
          Corbeille vide.
        </div>
      )}
      {trashAudits.length > 0 && (
        <div>
          <h3 className="font-bold text-sm text-slate-300 mb-2">Audits ({trashAudits.length})</h3>
          <div className="space-y-2">
            {trashAudits.map(a => (
              <TrashRow key={a.audit_id} entity={a} type="audit" label={a.title} subtitle={`Client: ${clientName(a.client_id)} · ${a.sections?.length || 0} sections`} onRestore={() => restore("audit", a.audit_id)} onPurge={() => purge("audit", a.audit_id)} />
            ))}
          </div>
        </div>
      )}
      {trashTemplates.length > 0 && (
        <div>
          <h3 className="font-bold text-sm text-slate-300 mb-2">Modeles ({trashTemplates.length})</h3>
          <div className="space-y-2">
            {trashTemplates.map(t => (
              <TrashRow key={t.template_id} entity={t} type="template" label={t.name} subtitle={`${t.sections?.length || 0} sections · mode ${t.mode}`} onRestore={() => restore("template", t.template_id)} onPurge={() => purge("template", t.template_id)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TrashRow({ entity, type, label, subtitle, onRestore, onPurge }) {
  const id = entity.audit_id || entity.template_id;
  return (
    <div className="bg-[#14101c] border border-white/10 rounded-lg p-4 flex items-center justify-between" data-testid={`trashed-${type}-${id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Trash className="w-4 h-4 text-red-400" />
          <span className="font-bold text-white">{label}</span>
        </div>
        <div className="text-xs text-slate-400 mt-0.5">{subtitle}</div>
        <div className="text-[10px] text-slate-500 mt-1">
          Supprime le {entity.deleted_at?.slice(0, 19).replace("T", " ")} · par {entity.deleted_by}
          {entity.deletion_reason && <> · motif: <span className="text-slate-300">"{entity.deletion_reason}"</span></>}
        </div>
      </div>
      <div className="flex gap-1">
        <Button size="sm" variant="ghost" onClick={onRestore} className="text-green-400" data-testid={`restore-${type}-${id}`}><RotateCcw className="w-4 h-4 mr-1" />Restaurer</Button>
        <Button size="sm" variant="ghost" onClick={onPurge} className="text-red-500" data-testid={`purge-${type}-${id}`}><Trash2 className="w-4 h-4 mr-1" />Purger</Button>
      </div>
    </div>
  );
}

// ─── Trail Tab (RGPD audit log) ────────────────────────────
function TrailTab({ trail, clientName }) {
  const actionColors = {
    soft_delete: "text-red-400 bg-red-500/10",
    restore: "text-green-400 bg-green-500/10",
    purge: "text-red-500 bg-red-500/20",
  };
  const actionLabel = { soft_delete: "Corbeille", restore: "Restaure", purge: "Purge" };
  return (
    <div className="space-y-2" data-testid="trail-tab">
      <div className="bg-slate-900 border border-white/10 rounded-xl p-4 text-xs text-slate-400 mb-2">
        Journal exhaustif des actions de suppression / restauration / purge. Conforme RGPD (qui, quoi, quand, pourquoi).
      </div>
      {trail.length === 0 ? (
        <div className="text-center py-10 text-slate-500 border border-dashed border-white/10 rounded-xl">Aucune entree</div>
      ) : trail.map(t => (
        <div key={t.trail_id} className="bg-[#14101c] border border-white/10 rounded p-3 text-sm flex items-center gap-3" data-testid={`trail-${t.trail_id}`}>
          <span className={`text-[10px] font-bold px-2 py-1 rounded ${actionColors[t.action] || "bg-white/5 text-slate-400"}`}>{actionLabel[t.action] || t.action}</span>
          <span className="text-slate-300 font-semibold">{t.entity_type}</span>
          <span className="text-slate-500 font-mono text-xs">{t.entity_id}</span>
          <span className="text-slate-400 text-xs flex-1 truncate">
            {t.extra?.title || t.extra?.name || ""}
            {t.reason && <span className="ml-2 text-slate-300">· motif: "{t.reason}"</span>}
          </span>
          <span className="text-[10px] text-slate-500 whitespace-nowrap">{t.actor_email}</span>
          <span className="text-[10px] text-slate-600 whitespace-nowrap">{t.timestamp?.slice(0, 16).replace("T", " ")}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Delete Motif Dialog (RGPD) ────────────────────────────
function DeleteMotifDialog({ ctx, api, onClose }) {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const confirm = async () => {
    if (!reason.trim()) { toast.error("Le motif est obligatoire (RGPD)"); return; }
    setLoading(true);
    try {
      const endpoint = ctx.type === "audit" ? `/audits/${ctx.id}` : `/audit-templates/${ctx.id}`;
      await api.delete(`${endpoint}?reason=${encodeURIComponent(reason)}`);
      toast.success("Deplace dans la corbeille");
      onClose(true);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(false); }}>
      <DialogContent className="max-w-md bg-[#0b0811] border border-white/10 text-white" data-testid="delete-motif-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Trash2 className="w-5 h-5 text-red-400" />Supprimer — motif requis (RGPD)</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-slate-300">
            Vous allez deplacer <b>{ctx.title}</b> dans la corbeille. Indiquez le motif :
          </div>
          <Textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Ex: Demande client RGPD, erreur d'encodage, doublon..."
            rows={3}
            className={inputCls}
            data-testid="delete-motif-input"
            autoFocus
          />
          <div className="text-[10px] text-slate-500">
            Cette action est tracee (auteur, date, motif) dans le journal RGPD. Vous pourrez restaurer l'element depuis la Corbeille.
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="ghost" onClick={() => onClose(false)}>Annuler</Button>
          <Button onClick={confirm} disabled={loading || !reason.trim()} className="bg-red-500 hover:bg-red-600 text-white font-bold" data-testid="delete-motif-confirm">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Deplacer dans la corbeille
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
